import React, { useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { _FeatureForPlan } from '../../../../helpers/subscriptionHelper';
import './FeatureCustomization.scss';

type FeatureConfiguration = {
    featureId: string;
    featureName: string;
    options: {
        [optionId: string]: {
            optionName: string;
            optionDescription: string;
            value: string;
            unit: string;
            availableUnits: string[];
            planFeatureId?: string; // ID from planFeatures array for existing features
        };
    };
};

type Props = {
    availableFeatures: _FeatureForPlan[];
    addedFeatures: FeatureConfiguration[];
    onAddFeature: (feature: FeatureConfiguration) => void;
    onRemoveFeature: (featureId: string) => void;
    onUpdateFeatureOption: (featureId: string, optionId: string, field: 'value' | 'unit', value: string) => void;
};

const FeatureCustomization = ({ availableFeatures, addedFeatures, onAddFeature, onRemoveFeature, onUpdateFeatureOption }: Props) => {
    const [selectedFeatureId, setSelectedFeatureId] = useState<string>('');
    const [expandedFeatures, setExpandedFeatures] = useState<{ [key: string]: boolean }>({});

    const handleAddFeature = () => {
        if (!selectedFeatureId) return;

        const feature = availableFeatures.find((f) => f.id === selectedFeatureId);
        if (!feature) return;

        // Check if feature is already added
        if (addedFeatures.some((f) => f.featureId === selectedFeatureId)) {
            return;
        }

        // Create initial configuration for the feature
        const featureConfig: FeatureConfiguration = {
            featureId: feature.id,
            featureName: feature.name,
            options: {},
        };

        // Initialize all feature options with default values
        feature.featureOptions.forEach((option) => {
            if (!option.optionUnitList) return;
            const defaultValue = option.optionUnitList[0] || '';
            const isToggleOption =
                option.optionUnitList.length === 2 &&
                option.optionUnitList.includes('enabled') &&
                option.optionUnitList.includes('disabled');

            featureConfig.options[option.id] = {
                optionName: option.optionName,
                optionDescription: option.optionDescription,
                value: isToggleOption ? 'enabled' : '10',
                unit: isToggleOption ? '' : defaultValue,
                availableUnits: option.optionUnitList,
            };
        });

        onAddFeature(featureConfig);
        setSelectedFeatureId('');
    };

    const toggleFeatureExpansion = (featureId: string) => {
        setExpandedFeatures((prev) => ({
            ...prev,
            [featureId]: !prev[featureId],
        }));
    };

    const isToggleOption = (units: string[]) => {
        return units.length === 2 && units.includes('enabled') && units.includes('disabled');
    };

    // Filter out already added features from dropdown
    const availableFeaturesForDropdown = availableFeatures.filter(
        (feature) => !addedFeatures.some((added) => added.featureId === feature.id)
    );

    const featureOptions = availableFeaturesForDropdown
        .map((feature) => ({ label: feature.name, value: feature.id }))
        .sort((a, b) => a.label.localeCompare(b.label));

    return (
        <div className="feature-customization-section">
            <div className="section-header mb-3">
                <h6 className="mb-1">Feature Customization</h6>
                <p className="text-muted small mb-0">Add features and configure their limits</p>
            </div>

            <div className="feature-selector mb-3">
                <label className="form-label">Select Feature</label>
                <div className="d-flex gap-2">
                    <Dropdown
                        value={selectedFeatureId}
                        options={featureOptions}
                        onChange={(e) => setSelectedFeatureId(e.value)}
                        placeholder="Select.."
                        className="flex-grow-1"
                    />
                    <button
                        type="button"
                        className="btn btn-success d-flex align-items-center justify-content-center"
                        onClick={handleAddFeature}
                        disabled={!selectedFeatureId}
                        style={{ width: '40px', height: '40px' }}
                    >
                        <i className="ri-check-line"></i>
                    </button>
                </div>
            </div>

            <div className="added-features-section">
                <h6 className="mb-2">Added Features ({addedFeatures.length})</h6>
                <p className="text-muted small mb-3">Configure limits for each feature</p>

                {addedFeatures.length === 0 ? (
                    <div className="text-center text-muted py-4">
                        <i className="ri-information-line" style={{ fontSize: '2rem' }}></i>
                        <p className="mt-2">No features added yet</p>
                    </div>
                ) : (
                    <div className="features-list">
                        {addedFeatures.map((feature) => {
                            const isExpanded = expandedFeatures[feature.featureId];
                            const hasOptions = Object.keys(feature.options).length > 0;

                            return (
                                <div key={feature.featureId} className="feature-card mb-3 feature-card-with-bg">
                                    <div className="feature-card-header">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center gap-2">
                                                {hasOptions && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 text-primary"
                                                        onClick={() => toggleFeatureExpansion(feature.featureId)}
                                                    >
                                                        <i
                                                            className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line`}
                                                            style={{ fontSize: '1.2rem' }}
                                                        ></i>
                                                    </button>
                                                )}
                                                <span className="fw-semibold text-primary">{feature.featureName}</span>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 text-danger"
                                                onClick={() => onRemoveFeature(feature.featureId)}
                                            >
                                                <i className="ri-delete-bin-line"></i>
                                            </button>
                                        </div>
                                    </div>

                                    {isExpanded && hasOptions && (
                                        <div className="feature-card-body mt-3">
                                            {Object.entries(feature.options).map(([optionId, option]) => {
                                                const isToggle = isToggleOption(option.availableUnits);

                                                return (
                                                    <div key={optionId} className="feature-option mb-3">
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <label className="form-label mb-0" style={{ flex: '0 0 120px' }}>
                                                                {option.optionName}
                                                            </label>
                                                            <div className="d-flex align-items-center gap-2" style={{ flex: 1 }}>
                                                                {isToggle ? (
                                                                    <InputSwitch
                                                                        checked={option.value === 'enabled'}
                                                                        onChange={(e) =>
                                                                            onUpdateFeatureOption(
                                                                                feature.featureId,
                                                                                optionId,
                                                                                'value',
                                                                                e.value ? 'enabled' : 'disabled'
                                                                            )
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <Dropdown
                                                                        value={option.unit}
                                                                        options={option.availableUnits.map((unit) => ({
                                                                            label: unit.charAt(0).toUpperCase() + unit.slice(1),
                                                                            value: unit,
                                                                        }))}
                                                                        onChange={(e) =>
                                                                            onUpdateFeatureOption(
                                                                                feature.featureId,
                                                                                optionId,
                                                                                'unit',
                                                                                e.value
                                                                            )
                                                                        }
                                                                        placeholder="Select limit"
                                                                        className="feature-option-dropdown"
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FeatureCustomization;
export type { FeatureConfiguration };
